import React, { useState } from 'react'
import { match } from 'ts-pattern'
import { useTranslation } from 'react-i18next'
import { Card, Stack, Tab, Tabs, useMediaQuery, useTheme } from '@mui/material'
import { FragmentType, graphql, useFragment } from '@/generated/gql'
import OverviewTab from './Overview'
import IngredientsTab from './Ingredients'
import NutritionalTab from './Nutritional'
import SourcingTab from './Sourcing'

const canteenMenuItemDetailItemDataFragment = graphql(`
  fragment CanteenMenuItemDetail_ItemData_TabsFragment on MenuItem {
    ...CanteenMenuItemIngredients_ItemDataFragmentV2
    ...CanteenMenuItemNutritional_ItemDataFragmentV2
    _id
  }
`)

type Props = {
  description: string | null
  itemData: FragmentType<typeof canteenMenuItemDetailItemDataFragment>
  companyName: string
  sourcing: string | null
}

const MenuItemDetailTabs: React.FC<Props> = ({
  description,
  itemData: itemDataProp,
  companyName,
  sourcing,
}) => {
  const theme = useTheme()
  const isNarrow = useMediaQuery(theme.breakpoints.down('sm'))

  const itemData = useFragment(canteenMenuItemDetailItemDataFragment, itemDataProp)

  const { t } = useTranslation()
  const [selectedTab, setSelectedTab] = useState(0)

  return (
    <Card sx={{ px: 2 }}>
      <Stack direction="column" position="sticky" top={0} bgcolor="background.paper" zIndex={3}>
        <Tabs
          value={selectedTab}
          onChange={(_, value) => setSelectedTab(value)}
          aria-label="item-detail-tabs"
          variant={isNarrow ? 'scrollable' : 'fullWidth'}
          centered={!isNarrow}
        >
          <Tab value={0} label={t('cc:overview')} />
          <Tab value={1} label={t('cc:ingredients')} />
          <Tab value={2} label={t('cc:nutritional')} />
          <Tab value={3} label={t('cc:sourcing')} />
        </Tabs>
      </Stack>
      <Stack direction="column" justifyContent="space-between" height="100%" padding={1}>
        {match(selectedTab)
          .with(0, () => <OverviewTab description={description} companyName={companyName} />)
          .with(1, () => <IngredientsTab itemData={itemData} companyName={companyName} />)
          .with(2, () => <NutritionalTab itemData={itemData} companyName={companyName} />)
          .with(3, () => <SourcingTab companyName={companyName} sourcing={sourcing} />)
          .otherwise(() => (
            <></>
          ))}
      </Stack>
    </Card>
  )
}

export default MenuItemDetailTabs
